import { LitElement, html } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import Storefront from 'utils/storefront';
import * as queries from 'utils/storefront/queries';

@customElement('product-form')
class ProductForm extends LitElement {
  @property({ type: String, attribute: 'final-sale-tag' })
  accessor finalSaleTag;

  @property({ type: String, attribute: 'drop-ship-tag' })
  accessor dropShipTag;

  @property({ type: String, attribute: 'discount-code-tag' })
  accessor discountCodeTag;

  @property({ type: String, attribute: 'product-tags' })
  accessor productTags;

  @property({ type: String, attribute: 'product-final-sale-metafield' })
  accessor productFinalSaleMetafield;

  get finalSaleValue() {
    return (
      this.productFinalSaleMetafield ||
      (this.productTags?.includes(this.finalSaleTag)
        ? this.finalSaleTag.replace(' ', '')
        : undefined)
    );
  }

  constructor() {
    super();
    this.variantAvailable = false;
    this.productBuyButton = this.querySelector('product-buy-button');
    this.productBackInStockForm = this.querySelector(
      'product-back-in-stock-form',
    );
    this.quantity = 1;
    this.monogramPayload = {};
    this.storefront = Storefront.getInstance();
  }

  connectedCallback() {
    super.connectedCallback();
    this.productTags =
      this.productTags && JSON.parse(decodeURI(this.productTags));
    this.variantIdInput = this.querySelector('input[name="id"]');
    this.addEventListener('pdp:variant-change', this._handleVariantChange);
    this.addEventListener('pdp:quantity-change', this._handleQuantityChange);
    document.addEventListener(
      'pdp:monogram-update',
      this._handleMonogramUpdate,
    );
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('pdp:variant-change', this._handleVariantChange);
    this.removeEventListener('pdp:quantity-change', this._handleQuantityChange);
    document.removeEventListener(
      'pdp:monogram-update',
      this._handleMonogramUpdate,
    );
  }

  _generateLineItemIdentifier = (itemIDs, attributes = {}, delimiter = '|') => {
    const itemIdentifiers = itemIDs.filter(Boolean);
    const itemAttributes = attributes;
    const identifiers = [
      ...itemIdentifiers,
      ...itemAttributes.map((attr) => attr.value),
    ];
    return identifiers.join(delimiter);
  };

  _handleMonogramUpdate = (event) => {
    if (!event.detail) {
      this.monogramPayload = {};
      return;
    }

    this.monogramPayload = event.detail;
  };

  _handleQuantityChange = (event) => {
    this.quantity = event.detail.quantity;
  };

  _handleVariantChange = (event) => {
    const { name, value, optionAvailable } = event.detail;
    if (name === 'variant') {
      this.variantIdInput.value = value;
      this.variantAvailable = optionAvailable;

      if (this.variantAvailable) {
        this.productBuyButton.classList.remove('hidden');
        this.productBackInStockForm.classList.add('hidden');
      } else {
        this.productBuyButton.classList.add('hidden');
        this.productBackInStockForm.classList.remove('hidden');
        this.productBackInStockForm.setAttribute('variant-id', value);
      }
    }
  };

  _handleFormSubmit = async (event) => {
    event.preventDefault();
    if (!this.variantAvailable) return;

    const form = event.target;
    const formData = new FormData(form);
    const data = Object.fromEntries(formData.entries());
    const { id: variantId } = data;
    const payload = await this._buildATCPayload({ mainVariantId: variantId });

    const addToCartEvent = new CustomEvent('cart:add', {
      detail: {
        payload,
        origin,
        component_name: '',
      },
    });
    document.dispatchEvent(addToCartEvent);
    const resetMonogramEvent = new CustomEvent('monogram:reset');
    document.dispatchEvent(resetMonogramEvent);
    this.monogramPayload = {};
  };

  _generateUUID() {
    return crypto.randomUUID();
  }

  _buildATCPayload = async ({ mainVariantId }) => {
    const { quantity, monogramPayload } = this;
    const mainVariantPayload = {
      merchandiseId: mainVariantId,
      quantity,
      attributes: await this._buildAttributes(),
    };

    if (Object.keys(monogramPayload).length) {
      mainVariantPayload.attributes.push(
        {
          key: '_use_function',
          value: `true`,
        },
        {
          key: '_tag_:final-sale',
          value: this.finalSaleValue || 'FinalSale',
        },
        {
          key: '_groupID',
          value: this._generateUUID(),
        },
        {
          key: '_monogram_data',
          value: monogramPayload.attributes.find(
            (attr) => attr.key === '_monogram_data',
          ).value,
        },
        {
          key: '_monogram_id',
          value: monogramPayload.merchandiseId,
        },
        {
          key: '_monogram_price',
          value: monogramPayload.price / 100,
        },
        {
          key: '_monogram_product_id',
          value: monogramPayload.monogramProductId,
        },
      );
      if (!this.finalSaleValue) {
        mainVariantPayload.attributes.push({
          key: '_isMonogramFinalSale',
          value: 'FinalSale',
        });
      }
    }

    return mainVariantPayload;
  };

  _buildAttributes = async () => {
    const attributes = [];
    const storefront = Storefront.getInstance();
    const productHandleElement = document.querySelector(
      '[data-product-handle]',
    );
    const productHandle = productHandleElement.getAttribute(
      'data-product-handle',
    );

    const responseByHandleUSD = await storefront.request({
      query: queries.productByHandleUSD(),
      variables: { handle: productHandle },
    });

    const usdCompareAtPrice =
      responseByHandleUSD?.data?.productByHandle?.compareAtPriceRange
        ?.minVariantPrice?.amount || '0.0';

    if (usdCompareAtPrice !== '0.0') {
      attributes.push({
        key: '_compare_at_price',
        value: parseFloat(usdCompareAtPrice).toFixed(2),
      });
    }

    if (this.finalSaleValue) {
      attributes.push({
        key: '_tag_:final-sale',
        value: this.finalSaleValue,
      });
    }
    if (this.productTags.includes(this.discountCodeTag)) {
      const tagObj = {
        key: '_tag_:discount-code',
        value: `${this.discountCodeTag}`,
      };
      attributes.push(tagObj);
    }
    if (this.productTags.includes(this.dropShipTag)) {
      attributes.push({
        key: '_tag_:drop-ship',
        value: `${this.dropShipTag.replace(' ', '')}`,
      });
    }

    return attributes;
  };

  render() {
    return html`<slot @submit=${this._handleFormSubmit}></slot>`;
  }
}

export default ProductForm;
